'use client';
import Image from 'next/image';

export interface FloatingCouponButtonProps {
  /** Number badge to display in the top-left of the icon. */
  count: number;
  /** True when there are bopis orders → raise the FAB above them. */
  hasBopisOrders: boolean;
  /** Optional admin-uploaded icon URL. Falls back to the default storefront asset when empty. */
  iconUrl?: string | null;
  onClick: () => void;
}

const DEFAULT_ICON = '/images/collectable-coupon.png';

/**
 * Floating action button that opens the collectable-coupon surface.
 *
 * Design: just the gift icon with a blue count badge. No background ring.
 * Hover scales the button slightly; active scales it down for tactile feedback.
 * When the admin has uploaded a custom icon, that image replaces the default gift.
 */
export const FloatingCouponButton = ({
  count,
  hasBopisOrders,
  iconUrl,
  onClick,
}: FloatingCouponButtonProps) => {
  const showCustomIcon = typeof iconUrl === 'string' && iconUrl.length > 0;
  const iconSrc = showCustomIcon ? (iconUrl as string) : DEFAULT_ICON;

  return (
    <div className={`fixed ${hasBopisOrders ? 'bottom-64' : 'bottom-40'} right-[40px] z-[100]`}>
      <button
        onClick={onClick}
        aria-label={`Open collectable coupons (${count} available)`}
        className="group relative flex h-20 w-20 items-center justify-center rounded-full transition-transform duration-200 hover:scale-105 active:scale-95 focus:outline-none focus:ring-4 focus:ring-blue-300/50"
      >
        <Image
          src={iconSrc}
          alt="Gift"
          width={64}
          height={64}
          className="h-16 w-16 object-contain drop-shadow-sm"
        />

        {/* Count badge */}
        <span className="absolute -right-1 -top-1 z-20 flex h-7 min-w-[1.75rem] items-center justify-center rounded-full bg-blue-600 px-1.5 text-xs font-bold text-white shadow-md ring-2 ring-white transition-transform group-hover:scale-110">
          {count}
        </span>
      </button>
    </div>
  );
};